import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCombobox } from "@mantine/core";
import { useCallback, useState } from "react";
import { showNotification } from "@mantine/notifications";
import { updateClients } from "../api";
import type { Client } from "../../types";
import { useNavigate } from "react-router-dom";
import { clientEventSystem, CLIENT_EVENTS } from "../events/clientEvents";
import { AppRoutes } from "@/constants.ts";

interface UpdateClientPayload {
  updatedData: Partial<Client>;
}

export const useClientStatus = (
  userId: string,
  initialStatus: string,
  taxYear?: number
) => {
  const queryClient = useQueryClient();
  const [currentStatus, setCurrentStatus] = useState(initialStatus);
  const combobox = useCombobox();
  const navigate = useNavigate();

  const updateClientMutation = useMutation<Client, Error, UpdateClientPayload>({
    mutationFn: async ({ updatedData }) => {
      const updatedClient = await updateClients(updatedData, userId);
      return updatedClient;
    },
    onSuccess: (data) => {
      const client = queryClient.getQueryData<Client>(["client", userId]);
      const mergedClient = client ? { ...client, ...data } : data;
      queryClient.setQueryData(["client", userId], mergedClient);

      queryClient.invalidateQueries({ queryKey: ["client", userId] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["analytics"] });
    },
    onError: (error: any) => {
      console.error("Error updating client:", error);

      // Show user-friendly error notification
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update client status";

      showNotification({
        title: "Status Update Failed",
        message: errorMessage,
        color: "red",
        autoClose: 5000,
      });
    },
  });

  const handleStatusUpdate = useCallback(
    async (newStatus: string) => {
      try {
        if (newStatus !== currentStatus) {
          // Validate that tax year is provided
          if (!taxYear) {
            showNotification({
              title: "Tax Year Required",
              message:
                "Cannot update status without a tax year. Please refresh the page and try again.",
              color: "red",
              autoClose: 5000,
            });
            return;
          }

          await updateClientMutation.mutateAsync({
            updatedData: {
              status: newStatus,
              taxYear: taxYear,
            },
          });

          setCurrentStatus(newStatus);

          if (newStatus === "Archived") {
            // Emit an event when a client is archived
            clientEventSystem.emit(CLIENT_EVENTS.CLIENT_ARCHIVED, userId);
            navigate(AppRoutes.clients);
          }
        }
      } catch (error) {
        console.error("Failed to update client status:", error);
      }
    },
    [currentStatus, updateClientMutation, taxYear, userId, navigate]
  );

  return {
    combobox,
    handleStatusUpdate,
    isUpdating: updateClientMutation.isPending,
    updateError: updateClientMutation.error,
  };
};
